import type { SgLabelTextVariant } from '@sg/ui-kit/core/src/lib/components/sg-label/sg-label.component';

export type SsiReasonTagVariant = SgLabelTextVariant;

/** The closed set of SSI selection reasons the backends can emit (as the enum *name* string). */
export type SsiSelectionReason = 'IcsdOverride' | 'PsetOverride' | 'InstrumentOverride' | 'CountryRule' | 'DefaultSsi';

// Single source of truth: label + badge variant per reason, keyed by the union so adding a sixth
// reason forces both to be filled in (no silently-out-of-sync label/variant). PSET Override =
// purple per Figma; the rest use distinct design-system colours.
const SSI_SELECTION_REASONS: Record<SsiSelectionReason, { label: string; variant: SsiReasonTagVariant }> = {
  IcsdOverride: { label: 'ICSD Override', variant: 'dusk' },
  PsetOverride: { label: 'PSET Override', variant: 'purple' },
  InstrumentOverride: { label: 'Instrument Override', variant: 'sky-blue' },
  CountryRule: { label: 'Country Rule', variant: 'turquoise' },
  DefaultSsi: { label: 'Default SSI', variant: 'neutral' },
};

// The backends carry the reason as a free-form string, not a swagger enum, so the lookups are
// keyed by `string` and fall back to the raw value for a reason we don't know about yet — that
// tolerance lives at the boundary, while the table above stays exhaustively typed. Both maps are
// derived from the single table so they can never drift apart.
export const SSI_SELECTION_REASON_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(SSI_SELECTION_REASONS).map(([reason, { label }]) => [reason, label])
);

export const SSI_SELECTION_REASON_VARIANTS: Record<string, SsiReasonTagVariant> = Object.fromEntries(
  Object.entries(SSI_SELECTION_REASONS).map(([reason, { variant }]) => [reason, variant])
);

export function ssiSelectionReasonLabel(reason?: string): string | undefined {
  return reason ? (SSI_SELECTION_REASON_LABELS[reason] ?? reason) : undefined;
}
